import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../../../context/CartContext';
import ProductBaseIngredients from './ProductBaseIngredients';
import StickyIngredientsSidebar from './StickyIngredientsSidebar';
import StickyIngredientsSidebarMobile from './StickyIngredientsSidebarMobile';
import RecipeHeader from './RecipeHeader';
import RecipeHero from './RecipeHero';
import RecipeDescription from './RecipeDescription';
import RecipeFullDescription from './RecipeFullDescription';
import RecipeIngredients from './RecipeIngredients';
import RecipePreparation from './RecipePreparation';
import RecipeBasesSpices from './RecipeBasesSpices';
import RecipeNewsletterCTA from './RecipeNewsletterCTA';
import RecipeImageModal from './RecipeImageModal';
import SEO from '../../SEO/SEO';
import TopNavBar from '../../Headers/TopNavBar';
import CategoryHeader from '../Category/CategoryHeader';
import Footer from '../../Footer/Footer';
import NewsletterModal from '../../Modal/NewsletterModal';
import FeedbackButton from '../../Feedback/FeedbackButton';
import { useRecipeData } from './hooks/useRecipeData';
import { useRecipeScrollDetection } from './hooks/useRecipeScrollDetection';
import { processIngredients as processIngredientsUtil, replaceLinkPlaceholder, getCategorySlug, getRecipeCanonicalPath, extractRecipeIdFromSlug } from '../../../utils/recipeUtils';

// Helper to get internal recipe ID from route param (slug or legacy id)
const getRecipeIdFromRoute = (recipeSlugParam) => {
  if (!recipeSlugParam) return null;
  return extractRecipeIdFromSlug(recipeSlugParam);
};

const RecipePage = () => {
  const { recipeSlug } = useParams(); // Supports both /przepis/:recipeSlug and /kuchnia/:categorySlug/:recipeSlug
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useCart();
  
  const recipeId = getRecipeIdFromRoute(recipeSlug);
  const { recipe, loading, error, isInWishlist, setIsInWishlist } = useRecipeData(recipeId);
  const { isStickyIngredientsVisible, ingredientsRef } = useRecipeScrollDetection(recipe, loading);
  
  // Base URL for absolute URLs (used for SEO and social sharing)
  const baseUrl = "https://www.autyzmodkuchni.pl";

  // Get absolute URL for recipe image - use same logic as getRecipeImageSrc but return absolute URL
  // Properly encode URL for social media sharing (WhatsApp, Facebook, etc.)
  const getAbsoluteImageUrl = (imagePath) => {
    // Use same logic as getRecipeImageSrc from recipeUtils
    let relativePath;
    
    if (!imagePath || imagePath.trim() === '') {
      // If no image, use default recipe image (not logo)
      relativePath = '/img/ciasta.jpg';
    } else if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      // Already absolute URL - encode it properly
      try {
        const url = new URL(imagePath);
        return url.href;
      } catch {
        return imagePath;
      }
    } else if (imagePath.startsWith('/img/')) {
      // Already has /img/ prefix
      relativePath = imagePath;
    } else if (imagePath.startsWith('/')) {
      // Path starting with / but not /img/ - add /img/ prefix
      relativePath = `/img${imagePath}`;
    } else {
      // Relative path without leading / - add /img/ prefix
      relativePath = `/img/${imagePath}`;
    }
    
    // Encode the path properly for social media (handle spaces and special chars)
    const encodedPath = relativePath.split('/').map(segment => {
      if (!segment) return segment;
      return encodeURIComponent(segment);
    }).join('/');
    
    // Convert relative path to absolute URL
    return `${baseUrl}${encodedPath}`;
  };

  // Generate keywords for recipe with new important keywords
  const generateRecipeKeywords = (recipe) => {
    if (!recipe) return '';
    
    const baseKeywords = [
      'dieta bezglutenowa autyzm', // Najważniejsze - na początku
      'dieta autyzm',
      'autyzm leczenie',
      recipe.name,
      'przepis bez glutenu',
      'przepis bez nabiału',
      'przepis bez cukru',
      recipe.category ? recipe.category.toLowerCase() : '',
      'dieta eliminacyjna',
      'autyzm',
      'przepisy dla autyzmu',
      'bezglutenowe przepisy',
      'bezmleczne dania',
      'zdrowe przepisy',
      'przepisy wspierające autyzm'
    ].filter(Boolean);
    
    return baseKeywords.join(', ');
  };

  // Generate Schema.org Recipe structured data
  const generateRecipeSchema = (recipe) => {
    if (!recipe) return null;
    
    // Process ingredients - clean HTML and extract text
    const { normalized } = processIngredientsUtil(recipe.ingredients);
    const recipeIngredients = (normalized || []).map(ing => {
      // Remove HTML tags and {LINK} placeholders for Schema.org
      return ing
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/\{LINK\}/g, 'uniwersalnej mieszanki mąk bezglutenowych')
        .trim();
    }).filter(Boolean);
    
    // Process preparation steps
    let recipeInstructions = [];
    if (recipe.preparation) {
      const cleanPreparation = recipe.preparation
        .replace(/zdrowego.*$/gis, '')
        .replace(/smacznego.*$/gis, '')
        .trim();
      const lines = cleanPreparation
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean);
      
      let stepCounter = 1;
      let currentStepText = '';
      
      lines.forEach(line => {
        // Skip "Na" headers
        if (line.match(/^Na\s+[^:]+:/i)) {
          return;
        }
        
        // Check if line starts with a number (new step)
        if (/^\d+\./.test(line)) {
          // Save previous step if exists
          if (currentStepText.trim()) {
            recipeInstructions.push({
              "@type": "HowToStep",
              "position": stepCounter++,
              "text": currentStepText.trim()
            });
          }
          // Start new step
          currentStepText = line.replace(/^\d+\.\s*/, '').trim();
        } else if (line.length > 0) {
          // Continue current step
          if (currentStepText) {
            currentStepText += ' ' + line;
          } else {
            currentStepText = line;
          }
        }
      });
      
      // Add last step
      if (currentStepText.trim()) {
        recipeInstructions.push({
          "@type": "HowToStep",
          "position": stepCounter,
          "text": currentStepText.trim()
        });
      }
      
      // Clean HTML from instructions
      recipeInstructions = recipeInstructions.map(step => ({
        ...step,
        text: step.text
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/\{LINK\}/g, 'uniwersalnej mieszanki mąk bezglutenowych')
          .trim()
      }));
    }

    const recipeSchema = {
      "@context": "https://schema.org",
      "@type": "Recipe",
      "name": recipe.name,
      "description": recipe.shortdesc || recipe.description || "",
      "image": getAbsoluteImageUrl(recipe.image),
      "recipeCategory": recipe.category || "",
      "recipeCuisine": "Polish",
      "recipeIngredient": recipeIngredients,
      "author": {
        "@type": "Organization",
        "name": "Autyzm od Kuchni"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Autyzm od Kuchni",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/img/logo.png`
        }
      },
      "url": typeof window !== 'undefined' ? window.location.href : `${baseUrl}${getRecipeCanonicalPath(recipe)}`
    };

    // Only add recipeInstructions if we have steps
    if (recipeInstructions.length > 0) {
      recipeSchema.recipeInstructions = recipeInstructions;
    }

    return recipeSchema;
  };

  // Generate BreadcrumbList structured data
  const generateBreadcrumbSchema = (recipe) => {
    if (!recipe) return null;

    const categorySlug = getCategorySlug(recipe.category);
    const categoryName = recipe.category || 'Przepisy';

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Strona główna",
          "item": `${baseUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Kuchnia",
          "item": `${baseUrl}/kuchnia`
        },
        ...(categorySlug ? [{
          "@type": "ListItem",
          "position": 3,
          "name": categoryName,
          "item": `${baseUrl}/kuchnia/${categorySlug}`
        }] : []),
        {
          "@type": "ListItem",
          "position": categorySlug ? 4 : 3,
          "name": recipe.name,
          "item": typeof window !== 'undefined' ? window.location.href : `${baseUrl}${getRecipeCanonicalPath(recipe)}`
        }
      ]
    };
  };
  
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isFullDescExpanded, setIsFullDescExpanded] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isStickyIngredientsOpen, setIsStickyIngredientsOpen] = useState(false);
  const [isStickyIngredientsOpenMobile, setIsStickyIngredientsOpenMobile] = useState(false);
  const [isBaseSpicesExpanded, setIsBaseSpicesExpanded] = useState(false);
  
  // Initialize isDesktop correctly - check on mount
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return false;
  });

  // Check if we're on desktop and update on resize
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    // Check immediately
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Handle escape key for image modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isImageModalOpen) {
        setIsImageModalOpen(false);
      }
    };
    
    if (isImageModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen]);

  // Newsletter modal logic
  useEffect(() => {
    if (!loading && recipe) {
      const hasSubscribed = localStorage.getItem('newsletter_subscribed') === 'true';
      if (hasSubscribed) return;
      
      const isMieszankaRecipe = recipe.id === 'mieszanka-2' || recipe.id?.includes('mieszanka');
      
      if (isMieszankaRecipe) {
        const timer = setTimeout(() => {
          setIsNewsletterModalOpen(true);
        }, 500);
        return () => clearTimeout(timer);
      }
      
      const recipeOpensKey = 'recipe_opens_count';
      let opensCount = parseInt(localStorage.getItem(recipeOpensKey) || '0', 10);
      opensCount += 1;
      localStorage.setItem(recipeOpensKey, opensCount.toString());
      
      if (opensCount >= 5) {
        const timer = setTimeout(() => {
          setIsNewsletterModalOpen(true);
          localStorage.setItem(recipeOpensKey, '0');
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, recipe]);

  const handleNewsletterSuccess = () => {
    localStorage.setItem('newsletter_subscribed', 'true');
    setIsNewsletterModalOpen(false);
  };

  const handleToggleWishlist = () => {
    if (recipe) {
      if (isInWishlist) {
        dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: recipe.id });
        setIsInWishlist(false);
      } else {
        dispatch({ type: 'ADD_TO_WISHLIST', payload: recipe });
        setIsInWishlist(true);
      }
    }
  };


  const processIngredients = (ingredientsData) => {
    return processIngredientsUtil(ingredientsData || recipe?.ingredients);
  };

  // If we arrived via legacy or category-based URL, redirect to canonical /przepis/:slug once data is loaded
  useEffect(() => {
    if (!recipe || loading || error) return;
    const canonicalPath = getRecipeCanonicalPath(recipe);
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [recipe, loading, error, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavBar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavBar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Przepis nie został znaleziony</h1>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Wróć do poprzedniej strony
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Generate SEO data
  const recipeKeywords = generateRecipeKeywords(recipe);
  const recipeSchema = generateRecipeSchema(recipe);
  const breadcrumbSchema = generateBreadcrumbSchema(recipe);
  
  // Combine structured data
  const structuredData = [recipeSchema, breadcrumbSchema].filter(Boolean);

  // Absolute canonical URL (no query/hash) for SEO
  const getRecipeCanonicalUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${window.location.pathname}`;
    }
    return `${baseUrl}${getRecipeCanonicalPath(recipe)}`;
  };

  return (
    <>
      <SEO 
        title={`${recipe.name} - Przepis bez glutenu, nabiału i cukru | Autyzm od Kuchni`}
        description={recipe.shortdesc || `${recipe.name} - przepis bez glutenu, nabiału i cukru idealny dla osób z autyzmem.`}
        keywords={recipeKeywords}
        ogType="article"
        ogImage={recipe?.image ? getAbsoluteImageUrl(recipe.image) : `${baseUrl}/img/ciasta.jpg`}
        canonical={getRecipeCanonicalUrl()}
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-gray-50">
        <div className="relative mb-8">
          <CategoryHeader showLogo={false} />
          <div className="absolute top-0 left-0 w-full">
            <TopNavBar />
          </div>
        </div>

        <RecipeHeader
          onBack={() => navigate(-1)}
          onToggleWishlist={handleToggleWishlist}
          isInWishlist={isInWishlist}
          recipe={recipe}
        />

        <div className="max-w-6xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
          >
            <RecipeHero 
              recipe={recipe} 
              onImageClick={() => setIsImageModalOpen(true)} 
            />

            <div className="p-6 sm:p-8">
              <RecipeDescription
                description={recipe.description}
                isExpanded={isDescriptionExpanded}
                onToggle={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              />

              <RecipeFullDescription
                fulldesc={recipe.fulldesc}
                isExpanded={isFullDescExpanded}
                onToggle={() => setIsFullDescExpanded(!isFullDescExpanded)}
              />

              {/* Hide main ingredients list when sticky sidebar is open on desktop */}
              {/* On desktop: hide when sidebar is open. On mobile: always show */}
              {(!isDesktop || !isStickyIngredientsOpen) && (
                <RecipeIngredients
                  ingredients={recipe.ingredients}
                  ingredientsRef={ingredientsRef}
                  onMobileButtonClick={() => setIsStickyIngredientsOpenMobile(true)}
                  isMobileButtonVisible={isStickyIngredientsVisible}
                />
              )}

              <RecipePreparation preparation={recipe.preparation} />

              <ProductBaseIngredients recipe={recipe} />

              <RecipeBasesSpices
                recipe={recipe}
                isExpanded={isBaseSpicesExpanded}
                onToggle={() => setIsBaseSpicesExpanded(!isBaseSpicesExpanded)}
              />

              <RecipeNewsletterCTA
                onOpenModal={() => setIsNewsletterModalOpen(true)}
              />
            </div>
          </motion.div>
        </div>
        
        <FeedbackButton isImageModalOpen={isImageModalOpen} />
        <Footer />
      </div>

      {/* Only render sticky sidebar on desktop */}
      {recipe?.ingredients && isDesktop && (
        <StickyIngredientsSidebar
          isVisible={isStickyIngredientsVisible}
          isOpen={isStickyIngredientsOpen}
          onOpen={() => setIsStickyIngredientsOpen(true)}
          onClose={() => setIsStickyIngredientsOpen(false)}
          ingredients={recipe.ingredients}
          processIngredients={processIngredients}
          replaceLinkPlaceholder={replaceLinkPlaceholder}
          isNewsletterModalOpen={isNewsletterModalOpen}
          isImageModalOpen={isImageModalOpen}
        />
      )}

      {/* Only render sticky sidebar on mobile */}
      {recipe?.ingredients && !isDesktop && (
        <StickyIngredientsSidebarMobile
          isVisible={isStickyIngredientsVisible}
          isOpen={isStickyIngredientsOpenMobile}
          onOpen={() => setIsStickyIngredientsOpenMobile(true)}
          onClose={() => setIsStickyIngredientsOpenMobile(false)}
          ingredients={recipe.ingredients}
          processIngredients={processIngredients}
          replaceLinkPlaceholder={replaceLinkPlaceholder}
          isNewsletterModalOpen={isNewsletterModalOpen}
          isImageModalOpen={isImageModalOpen}
        />
      )}

      <RecipeImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        recipe={recipe}
      />

      <NewsletterModal
        isOpen={isNewsletterModalOpen}
        onClose={() => setIsNewsletterModalOpen(false)}
        onSuccess={handleNewsletterSuccess}
      />
    </>
  );
};

export default RecipePage;
